import React, { useEffect } from "react";
import { withTranslation } from "react-i18next";
import { Box, Button, Grid, Typography } from "@mui/material";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import PropTypes from "prop-types";

import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";

const FloorWiseTenantDetails = ({ formik, t }) => {
  const {
    values: {
      floorWiseTenants,
      fireSafetyAddress: { floors },
    },
    setFieldValue,
    setFieldTouched,
  } = formik;

  const handleAddNewTenantDetail = (floorWiseTenants) => {
    setFieldValue("floorWiseTenants", [
      ...floorWiseTenants,
      {
        id: 0,
        name: "",
        mobileNumber: "",
        email: "",
        floorId: "",
        nameOfResponsiblePerson: "",
        designation: "",
      },
    ]);
    setFieldTouched(
      `floorWiseTenants[${floorWiseTenants.length}]`,
      false,
      false
    );
  };

  const handleRemoveTenantDetail = (index) =>
    setFieldValue(
      "floorWiseTenants",
      floorWiseTenants.filter((_, ind) => ind !== index)
    );

  useEffect(() => {
    if (!floors.length && floorWiseTenants.length) {
      setFieldValue("floorWiseTenants", []);
    }
    if (floors.length && floorWiseTenants.length) {
      const updatedTenantDetails = floorWiseTenants.filter(
        ({ floorId }) =>
          !floorId || floors.some(({ value }) => value === floorId)
      );
      setFieldValue(
        "floorWiseTenants",
        updatedTenantDetails.length ? updatedTenantDetails : []
      );
    }
  }, [floors]);

  return (
    <>
      <Grid item xs={12} className="border ps-3 pe-2 pb-2 pt-0" sx={{ mb: 3 }}>
        <Grid item xs={12} className="mt-3">
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
            <Box>
              <Typography className="mb-3 me-3" lineHeight={1} variant="h6">
                {t("Floor Wise Tenant Details")}
              </Typography>
            </Box>
            <Box>
              <Button
                color="primary"
                className="mb-3"
                sx={{ minWidth: "unset", textTransform: "capitalize" }}
                variant="outlined"
                onClick={() => handleAddNewTenantDetail(floorWiseTenants)}
              >
                <strong>
                  <span>+</span> {t("Add Tenant Details")}
                </strong>
              </Button>
            </Box>
          </Box>
        </Grid>

        {!!floorWiseTenants.length &&
          floorWiseTenants.map(
            (
              {
                name,
                mobileNumber,
                email,
                floorId,
                nameOfResponsiblePerson,
                designation,
              },
              index
            ) => (
              <Grid
                key={index}
                container
                columnSpacing={1}
                rowSpacing={1}
                sx={{ pl: 1 }}
                className="border p-2 pe-3 mt-2"
              >
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <SelectField
                    label={t("Floor Number")}
                    name={`floorWiseTenants.${index}.floorId`}
                    formik={formik}
                    options={floors}
                    placeholder="Select Floor Number"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Name of the Tenant") + "*"}
                    name={`floorWiseTenants.${index}.name`}
                    formik={formik}
                    value={name ?? ""}
                    placeholder="Enter Name"
                    disabled={!floorId}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Name of Responsible Person") + "*"}
                    name={`floorWiseTenants.${index}.nameOfResponsiblePerson`}
                    formik={formik}
                    value={nameOfResponsiblePerson ?? ""}
                    placeholder="Enter Name"
                    disabled={!floorId}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Designation") + "*"}
                    name={`floorWiseTenants.${index}.designation`}
                    formik={formik}
                    value={designation ?? ""}
                    placeholder="Enter Designation"
                    disabled={!floorId}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Mobile Number") + "*"}
                    name={`floorWiseTenants.${index}.mobileNumber`}
                    type="number"
                    formik={formik}
                    value={mobileNumber ?? ""}
                    placeholder="Enter Mobile Number"
                    disabled={!floorId}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <InputFieldText
                    label={t("Email") + "*"}
                    name={`floorWiseTenants.${index}.email`}
                    formik={formik}
                    value={email ?? ""}
                    placeholder="Enter Email"
                    disabled={!floorId}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <Button
                    color="error"
                    size="small"
                    sx={{
                      minWidth: "unset",
                      border: "1px solid",
                      height: "40px",
                      width: "100px",
                      mb: 2,
                    }}
                    onClick={() => handleRemoveTenantDetail(index)}
                  >
                    <strong className="d-flex justify-content-center">
                      <DeleteOutlineSharpIcon fontSize="small" />
                      {t("Remove")}
                    </strong>
                  </Button>
                </Grid>
              </Grid>
            )
          )}
      </Grid>
    </>
  );
};

FloorWiseTenantDetails.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(FloorWiseTenantDetails);
